import React, { useState } from "react";
import { toast } from "sonner";
import { Lock, KeyRound, Eye, EyeOff, Save, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function AccountPanel({ user }) {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [show, setShow] = useState({ current: false, next: false });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 8) {
      toast.error("La nueva contraseña debe tener al menos 8 caracteres");
      return;
    }
    if (form.new_password !== form.confirm) {
      toast.error("La confirmación no coincide");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Contraseña actualizada");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo actualizar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="account-panel" className="space-y-6 max-w-xl">
      <div>
        <h2 className="font-display font-semibold tracking-tighter text-2xl md:text-3xl">
          Mi cuenta
        </h2>
        <p className="mt-1 text-sm text-[#525252]">
          Sesión iniciada como{" "}
          <span className="font-semibold text-[#0a0a0a]">{user?.email}</span>.
        </p>
      </div>

      <div className="bg-white border border-black/8 p-6">
        <div className="flex items-center gap-3 mb-5 pb-4 border-b border-black/8">
          <div className="w-10 h-10 flex items-center justify-center bg-[#0a0a0a] text-white">
            <Lock size={16} strokeWidth={1.6} />
          </div>
          <div>
            <div className="font-display font-semibold text-lg tracking-tight">
              Cambiar contraseña
            </div>
            <div className="text-xs text-[#525252]">
              Mínimo 8 caracteres. Elige una contraseña fuerte y única.
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
          <PasswordInput
            label="Contraseña actual"
            value={form.current_password}
            onChange={(v) => setForm({ ...form, current_password: v })}
            visible={show.current}
            onToggle={() => setShow({ ...show, current: !show.current })}
            testId="current-password"
            autoComplete="current-password"
          />
          <PasswordInput
            label="Nueva contraseña"
            value={form.new_password}
            onChange={(v) => setForm({ ...form, new_password: v })}
            visible={show.next}
            onToggle={() => setShow({ ...show, next: !show.next })}
            testId="new-password"
            autoComplete="new-password"
          />
          <PasswordInput
            label="Confirma la nueva contraseña"
            value={form.confirm}
            onChange={(v) => setForm({ ...form, confirm: v })}
            visible={show.next}
            onToggle={() => setShow({ ...show, next: !show.next })}
            testId="confirm-password"
            autoComplete="new-password"
          />

          <div className="pt-2">
            <button
              type="submit"
              data-testid="change-password-submit"
              disabled={saving}
              className="btn-primary text-[0.7rem] px-4 py-3 disabled:opacity-60"
            >
              {saving ? (
                <>
                  <Loader2 size={14} strokeWidth={1.8} className="animate-spin" /> Guardando
                </>
              ) : (
                <>
                  <Save size={14} strokeWidth={1.8} /> Actualizar contraseña
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PasswordInput({ label, value, onChange, visible, onToggle, testId, autoComplete }) {
  return (
    <label className="block">
      <div className="text-[0.7rem] uppercase tracking-[0.18em] font-semibold text-[#0a0a0a] mb-1.5">
        {label}
      </div>
      <div className="relative">
        <KeyRound
          size={14}
          strokeWidth={1.5}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none"
        />
        <input
          type={visible ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required
          autoComplete={autoComplete}
          data-testid={testId}
          className="w-full bg-white border border-black/15 pl-9 pr-10 py-2.5 text-sm focus:outline-none focus:border-[#c40000] transition-colors"
        />
        <button
          type="button"
          onClick={onToggle}
          aria-label={visible ? "Ocultar" : "Mostrar"}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-[#525252] hover:text-[#0a0a0a]"
        >
          {visible ? <EyeOff size={14} strokeWidth={1.5} /> : <Eye size={14} strokeWidth={1.5} />}
        </button>
      </div>
    </label>
  );
}
